import { Link } from "react-router-dom";
import {
  TreePine, Scissors, Maximize2, Sprout, Pencil, Wind,
  Layers, Wrench, Package, Flower2, RefreshCw, Snowflake,
  Droplets, PaintBucket, ShieldCheck, ArrowRight
} from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1763222208594-58426d30d3c4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const services = [
  {
    icon: TreePine,
    title: "Landscaping",
    desc: "Full-service landscape design and installation. We create stunning outdoor environments tailored to your home's architecture and your personal style, using quality plants, materials, and expert craftsmanship.",
  },
  {
    icon: Scissors,
    title: "Cutting Grass",
    desc: "Regular, professional lawn mowing services to keep your grass at the ideal height. We use professional-grade equipment to deliver a clean, even cut every time.",
  },
  {
    icon: Maximize2,
    title: "Trimming Edges",
    desc: "Precision edging along driveways, walkways, and garden beds gives your lawn a sharp, polished look. We ensure clean boundary lines that elevate your curb appeal.",
  },
  {
    icon: Sprout,
    title: "Fertilizers",
    desc: "Custom fertilization programs to promote healthy, lush growth. We analyze your soil and select the right nutrients for your grass type and seasonal needs.",
  },
  {
    icon: Pencil,
    title: "Edging",
    desc: "Detailed edging services that create crisp, defined borders between your lawn, garden beds, and hardscaping features for a professionally maintained appearance.",
  },
  {
    icon: Wind,
    title: "Grooming",
    desc: "Comprehensive lawn and garden grooming including hedge trimming, shrub shaping, and general tidying to keep your outdoor spaces neat and presentable.",
  },
  {
    icon: Layers,
    title: "Sod & Soil",
    desc: "Professional sod installation and soil preparation for new lawns or repairs. We source premium quality sod and ensure proper grading for optimal drainage and growth.",
  },
  {
    icon: Wrench,
    title: "Maintenance",
    desc: "Seasonal and ongoing property maintenance packages designed to keep your outdoor spaces in peak condition year-round without the hassle.",
  },
  {
    icon: Package,
    title: "Mulching",
    desc: "Premium mulch application to garden beds for moisture retention, weed suppression, and a polished aesthetic. Available in various colours and materials.",
  },
  {
    icon: Flower2,
    title: "Planting",
    desc: "Expert selection and installation of trees, shrubs, perennials, and annuals. We choose plants suited to your climate, soil, and sun exposure for lasting beauty.",
  },
  {
    icon: RefreshCw,
    title: "Seasonal Cleanup",
    desc: "Spring and fall cleanup services including leaf removal, debris clearing, bed preparation, and winterization to get your property ready for the next season.",
  },
  {
    icon: Snowflake,
    title: "Snow Removal & Salting",
    desc: "Reliable residential snow clearing for driveways, walkways, and entrances. We also provide salting and sand application to prevent ice build-up and keep your property safe.",
  },
  {
    icon: PaintBucket,
    title: "Fence Staining",
    desc: "Professional fence staining and sealing services to protect your wood fence from weathering, UV damage, and moisture — extending its lifespan and refreshing its look.",
  },
  {
    icon: ShieldCheck,
    title: "Deck Cleaning & Driveway Sealing",
    desc: "Pressure washing and deep cleaning for decks and patios, plus professional driveway sealing to protect asphalt from cracking, water damage, and UV deterioration.",
  },
];

export default function Services() {
  return (
    <main data-testid="services-page">
      {/* Hero */}
      <section
        className="relative min-h-[45vh] flex items-center justify-center overflow-hidden"
        style={{ paddingTop: "80px" }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${HERO_BG}')` }}
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 text-center px-4 py-16">
          <span
            className="inline-block mb-3 px-4 py-1.5 bg-[#8dc63f]/20 text-[#8dc63f] text-xs font-semibold rounded-full uppercase tracking-widest border border-[#8dc63f]/30"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            What We Offer
          </span>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-3"
            style={{ fontFamily: "'Outfit', sans-serif", textShadow: "0 2px 20px rgba(0,0,0,0.4)" }}
          >
            Our Services
          </h1>
          <p className="text-white/80 text-base mt-4 max-w-xl mx-auto">
            Comprehensive residential landscaping and property maintenance across Mississauga & the GTA
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-28 bg-[#F9F8F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc) => (
              <div
                key={svc.title}
                data-testid={`service-detail-${svc.title.toLowerCase().replace(/\s/g, "-").replace(/&/g, "")}`}
                className="service-card bg-white border border-[#E2E8DF] rounded-xl p-7 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-[#F0F4EF] flex items-center justify-center mb-5">
                  <svc.icon size={24} color="#3a5c2e" strokeWidth={1.8} />
                </div>
                <h3
                  className="font-semibold text-lg text-[#1F291E] mb-2"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {svc.title}
                </h3>
                <p className="text-[#4A5D48] text-sm leading-relaxed">{svc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-16 text-center"
        style={{ background: "linear-gradient(135deg, #3a5c2e 0%, #2d4a24 100%)" }}
      >
        <div className="max-w-2xl mx-auto px-4">
          <h2
            className="text-2xl sm:text-3xl font-bold text-white mb-4"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Ready to Get Started?
          </h2>
          <p className="text-white/80 text-sm mb-7">
            Contact us today for a free, no-obligation estimate on any of our services.
          </p>
          <Link
            to="/contact"
            data-testid="services-cta-btn"
            className="btn-accent px-8 py-3.5 rounded-xl text-sm inline-flex items-center gap-2"
          >
            Get a Free Quote <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
}
